import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import TemplatesTab from "../admin/TemplatesTab";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { money, useLoad } from "../lib/util";
import { Btn, Card } from "../components/kit";
import { Logo } from "./Landing";
import SchoolsTab from "../admin/SchoolsTab";
import UsersTab from "../admin/UsersTab";
import PlansTab from "../admin/PlansTab";
import PaymentsTab from "../admin/PaymentsTab";
import LeadsTab from "../admin/LeadsTab";
import MapsTab from "../admin/MapsTab";
import DueSoon from "../admin/DueSoon";

const STATUS = { teste: "Em teste", ativa: "Ativa", em_atraso: "Em atraso", suspensa: "Bloqueada", cancelada: "Cancelada" };

function Summary() {
  const [s, reload] = useLoad(async () => (await api.get("/admin/summary")).data, []);
  useEffect(() => {
    const t = setInterval(reload, 10000);
    window.addEventListener("mapia:admin", reload);
    return () => {
      clearInterval(t);
      window.removeEventListener("mapia:admin", reload);
    };
  }, [reload]);
  if (!s) return null;
  const items = [["Escolas", s.schools], ["Solicitações novas", s.leads_new], ["Mensalidades em aberto", money(s.payments_open)], ["Pagamentos atrasados", s.payments_late]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {items.map(([l, v]) => (
        <Card key={l} data-testid={`admin-kpi-${l}`}>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{l}</div>
          <div className="font-heading text-2xl font-bold text-[#0B192C] mt-1">{v}</div>
        </Card>
      ))}
      <div className="col-span-2 lg:col-span-4 flex flex-wrap gap-2 text-xs">
        {Object.entries(s.by_status).map(([k, v]) => <span key={k} className="px-2.5 py-1 rounded-full bg-white border">{STATUS[k] || k}: <b>{v}</b></span>)}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tick, setTick] = useState(0);
  const enter = (school) => {
    localStorage.setItem("mapia_school", school.id);
    nav("/app");
  };
  return (
    <div className="min-h-screen">
      <header className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo light />
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:inline text-slate-300">{user.email} · Super administrador</span>
            <Btn variant="ghost" data-testid="logout-button" className="text-white hover:bg-white/10" onClick={() => logout().then(() => nav("/login"))}><LogOut className="w-4 h-4" />Sair</Btn>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 py-8">
        <h1 className="font-heading text-3xl font-bold text-[#0B192C] mb-6">Painel do super administrador</h1>
        <Summary key={tick} />
        <DueSoon />
        <Tabs defaultValue="schools" onValueChange={() => setTick((t) => t + 1)}>
          <TabsList className="bg-white border h-auto flex-wrap justify-start">
            {[["schools", "Escolas"], ["maps", "Mapas e oferta"], ["users", "Gestores"], ["plans", "Planos"], ["payments", "Assinaturas e pagamentos"], ["leads", "Solicitações"], ["templates", "Modelos de formulários"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="data-[state=active]:bg-[#FF6500] data-[state=active]:text-white">{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="schools"><SchoolsTab onEnter={enter} /></TabsContent>
          <TabsContent value="maps"><MapsTab /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="plans"><PlansTab /></TabsContent>
          <TabsContent value="payments"><PaymentsTab /></TabsContent>
          <TabsContent value="leads"><LeadsTab /></TabsContent>
          <TabsContent value="templates"><TemplatesTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export { STATUS };
